import { useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
} from "@mui/material";
import { toast } from "react-toastify";
import api from "api/axios";
import {
  createExtraoralFinding,
  updateExtraoralFinding,
} from "api/extraoralFinding";
import {
  ALL_SPECIALITIES_LABEL,
  VALUE_MAX,
  buildFindingPayload,
  cleanValues,
  specialityStateOf,
  toValueList,
  validateFindingForm,
} from "./extraoralFindingForm";

const ExtraoralFindingModal = ({ open, onClose, data }) => {
  const findingId = data?._id;
  const isUpdate = Boolean(findingId);

  const [name, setName] = useState("");
  const [values, setValues] = useState([]);
  const [valueHint, setValueHint] = useState("");
  const [speciality, setSpeciality] = useState([]);
  const [allSpecialities, setAllSpecialities] = useState(false);
  const [specialityOptions, setSpecialityOptions] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;

    setName(data?.name || "");
    setValues(toValueList(data?.values));
    setValueHint("");
    const state = specialityStateOf(data);
    setSpeciality(state.speciality);
    setAllSpecialities(state.allSpecialities);
  }, [open, data]);

  useEffect(() => {
    if (!open) return;

    api
      .get("/specialization")
      .then((res) => {
        const list = res?.data?.data?.Specialization;
        setSpecialityOptions(Array.isArray(list) ? list : []);
      })
      .catch(() => toast.error("Could not load specialities"));
  }, [open]);

  const validationError = useMemo(
    () => validateFindingForm({ name, values, speciality, allSpecialities }),
    [name, values, speciality, allSpecialities]
  );

  const handleValuesChange = (next) => {
    const { values: cleaned, duplicates } = cleanValues(next);
    setValues(cleaned);
    setValueHint(duplicates.length ? `"${duplicates[0]}" is already added.` : "");
  };

  const handleSubmit = async () => {
    if (validationError) return;

    setSaving(true);
    const payload = buildFindingPayload({
      name,
      values,
      speciality,
      allSpecialities,
    });

    try {
      if (isUpdate) {
        await updateExtraoralFinding(findingId, payload);
        toast.success("Finding updated successfully");
      } else {
        await createExtraoralFinding(payload);
        toast.success("Finding created successfully");
      }
      onClose("save");
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
          `Failed to ${isUpdate ? "update" : "create"} finding`
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => !saving && onClose("close")}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle sx={{ fontWeight: 600 }}>
        {isUpdate ? "Edit Finding" : "Add Finding"}
      </DialogTitle>

      <DialogContent dividers>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2.5, pt: 1 }}>
          <TextField
            label="Finding"
            required
            fullWidth
            size="small"
            value={name}
            onChange={(event) => setName(event.target.value)}
            inputProps={{ "data-testid": "finding-name" }}
          />

          <Autocomplete
            multiple
            freeSolo
            options={[]}
            size="small"
            value={values}
            onChange={(event, next) => handleValuesChange(next)}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  size="small"
                  label={option}
                  {...getTagProps({ index })}
                  key={option}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Multiple Values"
                placeholder="Type and press Enter"
                inputProps={{ ...params.inputProps, maxLength: VALUE_MAX }}
                error={Boolean(valueHint)}
                helperText={
                  valueHint || "Press Enter after each value (e.g. Symmetrical)."
                }
              />
            )}
          />

          <Autocomplete
            multiple
            disableCloseOnSelect
            size="small"
            options={specialityOptions}
            value={speciality}
            disabled={allSpecialities}
            getOptionLabel={(option) => option?.name || ""}
            isOptionEqualToValue={(option, value) => option._id === value._id}
            onChange={(event, next) => setSpeciality(next)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Specialities"
                placeholder={
                  allSpecialities ? ALL_SPECIALITIES_LABEL : "Select specialities"
                }
              />
            )}
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={allSpecialities}
                onChange={(event) => {
                  const checked = event.target.checked;
                  setAllSpecialities(checked);
                  if (checked) setSpeciality([]);
                }}
              />
            }
            label={`${ALL_SPECIALITIES_LABEL} (every eligible dental doctor)`}
          />

          {validationError && (
            <Box role="alert" sx={{ color: "#d32f2f", fontSize: "0.8rem" }}>
              {validationError}
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ padding: "1rem 1.5rem" }}>
        <Button onClick={() => onClose("close")} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving || Boolean(validationError)}
          sx={{ bgcolor: "#194AF5", ":hover": { bgcolor: "#1540d6" } }}
        >
          {saving ? <CircularProgress size="1.2rem" sx={{ color: "#fff" }} /> : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExtraoralFindingModal;
